<template>
  <TournamentCard
    v-if="raceData"
    :offer="snippet.format(raceData.prizeTitle)"
    :title="snippet.format(raceData.title)"
    :active-button-title="activeButtonTitle"
    :image-front="raceData.bannerCardFront"
    :image-back="raceData.bannerCardBack"
    :is-finished-race="isTournamentsFinished && !isTournamentActive"
    :ribbon-color="cardLabelColor"
    :timer-end-time="endTime"
    :timer-title="timerTitle"
    :ribbon-title="raceLabelTitle"
    active-button-type="primary-blue"
    :status-data="statusBonusData"
    :is-coming-soon="isComingSoon"
    :provider-logo="raceData.providerImage"
    @cardClick="openRaceModal()"
    @buttonClick="openRaceModal()"
  />
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { TournamentCard, type TournamentStatusData } from '~/components/lib/tournaments';
import { useSnippet } from '~/composables/useSnippet';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreSsTournaments } from '~/stores/softswiss/tournaments';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { EModalName } from '~/types/modals';
import { ERaceStatus } from '~/types/races';
import type { ComponentInfoPageBlocksRacesBlock } from '~/types/infoPages';

const props = defineProps({
  raceSsId: {
    type: String,
    required: true,
  },
  data: {
    type: Object as PropType<ComponentInfoPageBlocksRacesBlock>,
    default: null,
  },
  isTournamentsFinished: {
    type: Boolean,
    default: false,
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
});

const storeSsTournaments = useStoreSsTournaments();
const storeModals = useStoreModals();
const storeComponents = useStoreComponents();
const storeButtonTitles = useStoreButtonTitles();

const snippet = useSnippet();

const isJoined = ref(false);

const tournamentExtended = computed(() =>
  storeSsTournaments.tournamentsExtended.find(
    (t) => t.tournamentData.frontend_identifier?.toLowerCase() === props.raceSsId.toLowerCase()
  )
);

const statusBonusData = computed((): TournamentStatusData | null => {
  if (isJoined.value && isTournamentActive.value) {
    return {
      statusIcon: storeComponents.races.iconJoined.url,
      statusText: storeComponents.races.congratulationsMessageText,
      status: 'success',
    };
  }
  return null;
});

const status = computed(() => tournamentExtended.value?.tournamentData.status);

const isTournamentActive = computed(
  () => tournamentExtended.value?.tournamentData.status === ERaceStatus.active
);

const endTime = computed(() => {
  const timestamp =
    status.value === ERaceStatus.active
      ? tournamentExtended.value?.tournamentData.end_at
      : tournamentExtended.value?.tournamentData.start_at;

  return timestamp ? new Date(timestamp) : undefined;
});

const raceData = computed(() => tournamentExtended.value?.raceData);
const activeButtonTitle = computed(() => storeButtonTitles.moreInfo);
const timerTitle = computed(() => storeSsTournaments.getRaceTimerTitle(status.value));
const cardLabelColor = computed(() =>
  status.value === ERaceStatus.scheduled
    ? 'grey'
    : storeSsTournaments.getRaceLabelColor(tournamentExtended.value?.raceData.raceLabel)
);
const raceLabelTitle = computed(() =>
  status.value === ERaceStatus.scheduled
    ? props.data?.scheduledTitle
    : storeSsTournaments.getRaceLabelTitle(tournamentExtended.value?.raceData.raceLabel)
);

const openRaceModal = () => {
  storeModals.openModal(EModalName.race, { [EModalName.race]: props.raceSsId });
};
watch(
  () => storeSsTournaments.tournamentsStatuses,
  (val) => {
    isJoined.value = Boolean(val.find((p) => p.frontId === props.raceSsId)?.status);
  },
  { immediate: true, deep: true }
);
</script>
